import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Mail, Paperclip, Send, X } from 'lucide-react';
import { FEEDBACK_LIMITS } from '@vagusx/clients';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { SheetActions, SheetBody } from '@/components/custom/sheet-layout';
import { UnsavedChangesDialog } from '@/components/custom/unsaved-changes-dialog';
import { useSubmitFeedback } from '@/hooks/use-submit-feedback';

interface FeedbackSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ACCEPTED_TYPES = 'image/*,.pdf,.csv,.xlsx,.xls,.txt,.json,.zip,.log,.md';
const FORM_ID = 'feedback-form';

export function FeedbackSheet({ open, onOpenChange }: FeedbackSheetProps): React.ReactElement {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mutation = useSubmitFeedback();

  useEffect(() => {
    if (open) {
      setSubject('');
      setMessage('');
      setAttachments([]);
      setShowDiscardDialog(false);
    }
  }, [open]);

  const isDirty = subject.trim().length > 0 || message.trim().length > 0 || attachments.length > 0;

  const addFiles = useCallback((files: File[]) => {
    setAttachments((prev) => [...prev, ...files].slice(0, FEEDBACK_LIMITS.maxAttachments));
  }, []);

  const removeAttachment = (index: number): void => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handlePaste = useCallback(
    (e: React.ClipboardEvent): void => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const imageFiles: File[] = [];
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            imageFiles.push(new File([file], `screenshot-${Date.now()}.png`, { type: file.type }));
          }
        }
      }
      if (imageFiles.length > 0) {
        e.preventDefault();
        addFiles(imageFiles);
      }
    },
    [addFiles]
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (e.target.files) addFiles(Array.from(e.target.files));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const canSubmit = subject.trim().length > 0 && message.trim().length > 0 && !mutation.isPending;

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!canSubmit) return;

    try {
      await mutation.mutateAsync({
        payload: {
          subject: subject.trim(),
          message: message.trim(),
          context: collectContext(),
        },
        attachments: attachments.length > 0 ? attachments : undefined,
      });
      toast.success('Feedback sent — thank you!');
      onOpenChange(false);
    } catch {
      // Global mutation error handler in MutationCache.onError surfaces a toast.
    }
  };

  // Sheet open-state changes go through this guard so close attempts
  // (ESC, overlay click, Cancel button) prompt before discarding work.
  // mid-submit closes are blocked outright.
  const handleOpenChange = (next: boolean): void => {
    if (next) {
      onOpenChange(true);
      return;
    }
    if (mutation.isPending) return;
    if (isDirty) {
      setShowDiscardDialog(true);
      return;
    }
    onOpenChange(false);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 sm:max-w-[560px]">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Send feedback
            </SheetTitle>
            <SheetDescription>
              Tell us about a bug, suggestion, or anything you'd like us to know. The current page,
              your browser, and your account are included automatically.
            </SheetDescription>
          </SheetHeader>

          <form
            id={FORM_ID}
            onSubmit={handleSubmit}
            onPaste={handlePaste}
            className="flex min-h-0 flex-1 flex-col"
          >
            <SheetBody className="flex flex-col gap-4 pt-2">
              <div className="space-y-1.5">
                <Label htmlFor="feedback-subject">Subject</Label>
                <Input
                  id="feedback-subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Brief description"
                  maxLength={FEEDBACK_LIMITS.maxSubjectLength}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="feedback-message">Message</Label>
                <Textarea
                  id="feedback-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="What happened? What did you expect?"
                  className="min-h-[200px]"
                  maxLength={FEEDBACK_LIMITS.maxMessageLength}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Tip: paste a screenshot directly from your clipboard.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label>Attachments</Label>
                <div className="flex items-center gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={ACCEPTED_TYPES}
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={attachments.length >= FEEDBACK_LIMITS.maxAttachments}
                  >
                    <Paperclip className="mr-2 h-4 w-4" />
                    Attach files
                  </Button>
                  <span className="text-xs text-muted-foreground">
                    Up to {FEEDBACK_LIMITS.maxAttachments} files
                  </span>
                </div>

                {attachments.length > 0 && (
                  <ul className="mt-2 flex flex-wrap gap-2">
                    {attachments.map((file, index) => (
                      <li
                        key={`${file.name}-${index}`}
                        className="flex items-center gap-2 rounded border bg-muted/50 px-2 py-1 text-sm"
                      >
                        <Paperclip className="h-3 w-3 text-muted-foreground" />
                        <span className="max-w-[160px] truncate">{file.name}</span>
                        <button
                          type="button"
                          onClick={() => removeAttachment(index)}
                          className="text-muted-foreground hover:text-foreground"
                          aria-label={`Remove ${file.name}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </SheetBody>
          </form>

          <SheetActions>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" form={FORM_ID} disabled={!canSubmit}>
              {mutation.isPending ? (
                'Sending…'
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Send
                </>
              )}
            </Button>
          </SheetActions>
        </SheetContent>
      </Sheet>

      <UnsavedChangesDialog
        open={showDiscardDialog}
        onCancel={() => setShowDiscardDialog(false)}
        onDiscard={() => {
          setShowDiscardDialog(false);
          onOpenChange(false);
        }}
      />
    </>
  );
}

function collectContext(): {
  url: string;
  userAgent: string;
  viewport?: string;
  timestamp: string;
} {
  const hasWindow = typeof window !== 'undefined';
  return {
    url: hasWindow ? window.location.href : '',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    viewport: hasWindow ? `${window.innerWidth}x${window.innerHeight}` : undefined,
    timestamp: new Date().toISOString(),
  };
}
